import React, { useState, useEffect } from "react";
import "./downloadedReports.css";
import {
  getDownloadedReports,
  getDownloadedReportPdf,
  deleteDownloadedReport,
} from "../../services/downloadedReportsService";

export interface DownloadedReport {
  id: string;
  plantName: string;
  email: string;
  occupation: string;
  usage: string;
  createdAt: string;
}

interface DownloadedReportsProps {
  isOpen: boolean;
  onClose: () => void;
}

const formatDate = (isoDate: string): string =>
  new Date(isoDate).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const DownloadedReports: React.FC<DownloadedReportsProps> = ({
  isOpen,
  onClose,
}) => {
  const [reports, setReports] = useState<DownloadedReport[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchReports();
    }
  }, [isOpen]);

  const fetchReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getDownloadedReports();
      const rows = response.data ?? [];
      setReports(rows);
    } catch (err: any) {
      console.error("Error fetching downloaded reports:", err);
      setReports([]);
      setError(
        err?.response?.data?.message ||
          "Failed to load downloaded reports."
      );
    } finally {
      setLoading(false);
    }
  };

  const saveBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownload = async (report: DownloadedReport) => {
    setDownloadingId(report.id);
    try {
      const response = await getDownloadedReportPdf(report.id);
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "application/pdf" });
      saveBlob(blob, `${report.plantName}-${report.id}.pdf`);
    } catch (err: any) {
      console.error("Error downloading stored report:", err);
      setError(
        err?.response?.data?.message ||
          "Failed to download the report."
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (report: DownloadedReport) => {
    const ok = window.confirm(
      `Delete the download record for ${report.email} (${report.plantName})? This cannot be undone.`
    );
    if (!ok) return;
    setDeletingId(report.id);
    setError(null);
    try {
      await deleteDownloadedReport(report.id);
      setReports((prev) => prev.filter((r) => r.id !== report.id));
    } catch (err: any) {
      console.error("Error deleting downloaded report:", err);
      setError(
        err?.response?.data?.message ||
          "Failed to delete the record."
      );
    } finally {
      setDeletingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="downloaded-reports-overlay" onClick={onClose}>
      <div className="downloaded-reports-wrapper">
        <button className="downloaded-reports-close" onClick={onClose}>
          ×
        </button>
        <div
          className="downloaded-reports-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="downloaded-reports-content">
            <h2 className="downloaded-reports-title">Downloaded Reports</h2>
            <p className="downloaded-reports-description">
              Users who downloaded an analysis report.
            </p>

            {loading && (
              <p className="downloaded-reports-status">Loading reports…</p>
            )}
            {!loading && error && (
              <p className="downloaded-reports-status downloaded-reports-error">
                {error}
              </p>
            )}
            {!loading && !error && reports.length === 0 && (
              <p className="downloaded-reports-status">
                No downloaded reports yet.
              </p>
            )}

            {!loading && !error && reports.length > 0 && (
              <div className="downloaded-reports-table-container">
                <table className="downloaded-reports-table">
                  <thead>
                    <tr className="downloaded-reports-header-row">
                      <th className="downloaded-reports-header">
                        Plant Name
                      </th>
                      <th className="downloaded-reports-header">Email</th>
                      <th className="downloaded-reports-header">
                        Occupation
                      </th>
                      <th className="downloaded-reports-header">Use</th>
                      <th className="downloaded-reports-header">Date</th>
                      <th className="downloaded-reports-header">Report</th>
                      <th className="downloaded-reports-header">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((report) => (
                      <tr
                        key={report.id}
                        className="downloaded-reports-row"
                      >
                        <td className="downloaded-reports-cell">
                          {report.plantName}
                        </td>
                        <td className="downloaded-reports-cell">
                          {report.email}
                        </td>
                        <td className="downloaded-reports-cell">
                          {report.occupation}
                        </td>
                        <td className="downloaded-reports-cell">
                          {report.usage}
                        </td>
                        <td className="downloaded-reports-cell downloaded-reports-date">
                          {formatDate(report.createdAt)}
                        </td>
                        <td className="downloaded-reports-cell">
                          <button
                            className="downloaded-reports-download-btn"
                            onClick={() => handleDownload(report)}
                            disabled={downloadingId === report.id}
                          >
                            {downloadingId === report.id
                              ? "Downloading…"
                              : "⭳ Download PDF"}
                          </button>
                        </td>
                        <td className="downloaded-reports-cell">
                          <button
                            className="downloaded-reports-delete-btn"
                            onClick={() => handleDelete(report)}
                            disabled={deletingId === report.id}
                            title="Delete this record (GDPR erasure)"
                          >
                            {deletingId === report.id ? "…" : "✕"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadedReports;
